/**
 * Theme runtime.
 *
 * Turns the static {@link tokens} object into live CSS custom properties on
 * the document root, and exposes typed helpers for the rare cases where a
 * value must be read in JS/TS rather than through a Tailwind class.
 */
import { colors, tokens, type ColorRole } from "./tokens";

export * from "./tokens";

/** Registry of named themes. The architecture supports many; today there is
 *  one deliberate, opinionated theme. Additional themes need only supply a
 *  colour map with the same {@link ColorRole} keys. */
export type ThemeName = "black";

const themeColors: Record<ThemeName, Record<ColorRole, string>> = {
  black: colors,
};

export const availableThemes: ThemeName[] = Object.keys(themeColors) as ThemeName[];

/**
 * Emit the given theme as CSS custom properties on `:root`.
 * Called once at startup and again whenever the theme changes.
 */
export function applyTheme(name: ThemeName): void {
  const root = document.documentElement;
  const map = themeColors[name];

  for (const [role, value] of Object.entries(map)) {
    root.style.setProperty(`--color-${role}`, value);
  }

  // Non-colour tokens that raw CSS needs to reach.
  root.style.setProperty("--font-sans", tokens.typography.fontFamily.sans);
  root.style.setProperty("--font-mono", tokens.typography.fontFamily.mono);
  root.style.setProperty("--radius-sm", tokens.radius.sm);
  root.style.setProperty("--radius-md", tokens.radius.md);
  root.style.setProperty("--radius-lg", tokens.radius.lg);
  root.style.setProperty("--duration-fast", tokens.motion.duration.fast);
  root.style.setProperty("--duration-base", tokens.motion.duration.base);
  root.style.setProperty("--easing-standard", tokens.motion.easing.standard);

  root.dataset.theme = name;
}

/** Reference a themed colour as a CSS `var()` expression. */
export function cssColor(role: ColorRole): string {
  return `var(--color-${role})`;
}

/** Build a standard transition string for one or more properties. */
export function transition(
  properties: string[],
  duration: keyof typeof tokens.motion.duration = "base",
  easing: keyof typeof tokens.motion.easing = "standard",
): string {
  const d = tokens.motion.duration[duration];
  const e = tokens.motion.easing[easing];
  return properties.map((p) => `${p} ${d} ${e}`).join(", ");
}
